<template>
  <div class="project-finder-wrapper">
    <!-- Expand Button (Collapsed State) -->
    <transition name="expand-button">
      <button
        v-if="panelCollapsed"
        class="expand-button"
        @click="togglePanel"
        aria-label="Open project finder"
        :aria-expanded="!panelCollapsed"
      >
        <theme-icon icon="search" type="icons" classes="w-5 h-5" />
      </button>
    </transition>

    <!-- Panel Overlay -->
    <transition name="panel-slide">
      <div v-if="!panelCollapsed" class="project-finder-overlay">
        <div class="project-finder-content">
          <!-- Panel Header -->
          <div class="project-finder-header">
            <div class="header-content">
              <div class="header-text">
                <h2 class="panel-title">Project Finder</h2>
                <p class="panel-subtitle">Select a project to view properties</p>
              </div>
              <div class="header-actions">
                <button
                  class="minimize-button"
                  @click="togglePanel"
                  aria-label="Close project finder"
                  :aria-expanded="!panelCollapsed"
                >
                  <theme-icon icon="close" type="icons" classes="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <!-- Panel Body -->
          <div class="project-finder-body">
            <!-- Project Selection Section -->
            <div class="project-selection-section">
              <label for="project-select" class="section-label">
                Select Project
              </label>
              
              <!-- Loading State -->
              <div v-if="loadingProjects" class="loading-state">
                <svg class="loading-spinner" viewBox="0 0 24 24">
                  <circle class="spinner-circle" cx="12" cy="12" r="10" fill="none" stroke-width="3"/>
                </svg>
                <span class="loading-text">Loading projects...</span>
              </div>
              
              <!-- Error State -->
              <div v-else-if="error" class="error-state">
                <p class="error-message">{{ error }}</p>
                <themed-button 
                  variant="outline" 
                  size="sm" 
                  @click="fetchProjects"
                >
                  Retry
                </themed-button>
              </div>
              
              <!-- Project Dropdown -->
              <themed-select
                v-else
                id="project-select"
                v-model="selectedProjectId"
                :options="projectOptions"
                placeholder="Select a project"
                @input="onProjectSelected"
              />
            </div>

            <!-- Property Display Section -->
            <div class="property-display-section">
              <!-- Loading State -->
              <div v-if="loadingProperties" class="loading-state">
                <svg class="loading-spinner" viewBox="0 0 24 24">
                  <circle class="spinner-circle" cx="12" cy="12" r="10" fill="none" stroke-width="3"/>
                </svg>
                <span class="loading-text">Loading properties...</span>
              </div>
              
              <!-- Properties Grid -->
              <themed-grid
                v-else
                :data="properties"
                :fields="gridFields"
                :actions="gridActions"
                :columns="2"
                :per-page="perPage"
                :pagination="true"
                :loading="loadingProperties"
                item-key="id"
                :empty-message="selectedProjectId ? 'No properties found in this project' : 'Select a project to view properties'"
                empty-icon="home-icon"
                @action="handleGridAction"
              />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedGrid from '../ThemedGrid.vue';

export default {
  name: 'ProjectFinder',
  
  components: {
    ThemeIcon,
    ThemedSelect,
    ThemedButton,
    ThemedGrid
  },
  
  props: {
    // Map instance for marker management
    map: {
      type: Object,
      required: true
    },
    
    // Whether the panel is visible
    visible: {
      type: Boolean,
      default: false
    },
    
    // Initial project ID (optional)
    initialProjectId: {
      type: Number,
      default: null
    }
  },
  
  data() {
    return {
      // Project selection
      selectedProjectId: null,
      projects: [],
      loadingProjects: false,
      
      // Property data
      properties: [],
      loadingProperties: false,
      
      // Map markers
      propertyMarkers: [],
      
      // UI state
      panelCollapsed: true,
      error: null,
      loading: false, // General loading state for actions
      
      // Pagination
      currentPage: 1,
      perPage: 8,
      
      // Request cancellation
      projectsAbortController: null,
      propertiesAbortController: null
    };
  },
  
  computed: {
    /**
     * Format projects for ThemedSelect options
     */
    projectOptions() {
      return this.projects.map(project => ({
        label: project.name,
        value: project.id
      }));
    },
    
    /**
     * Grid field configuration for property cards
     * Copied from ManageComps modal for consistency
     */
    gridFields() {
      return [
        // Image
        {
          field: 'image_url',
          type: 'image',
          defaultImage: '/img/temp/building-sample.jpg'
        },
        // Title
        {
          field: 'name',
          type: 'title'
        },
        // Subtitle
        {
          field: 'location',
          type: 'subtitle'
        },
        // Display fields
        {
          field: 'available_square_footage',
          label: 'AVAILABLE SQ. FT.',
          type: 'number'
        },
        {
          field: 'total_square_footage',
          label: 'TOTAL SQ. FT.',
          type: 'number'
        },
        {
          field: 'type.type',
          label: 'TYPE',
          type: 'text',
          transform: (value) => value || '-'
        }
      ];
    },
    
    /**
     * Grid action buttons configuration
     * Copied from ManageComps modal for consistency
     */
    gridActions() {
      return [
        {
          name: 'request',
          label: 'Request Details',
          icon: 'download',
          variant: 'default',
          visible: (item) => {
            return item.is_imported && 
                   (!item.scraper_status || item.scraper_status === 'failed') && 
                   !item.building_id;
          }
        },
        {
          name: 'status',
          label: 'Check Status',
          icon: 'refresh',
          variant: 'outline',
          visible: (item) => {
            return item.is_imported && 
                   item.scraper_status && 
                   item.scraper_status !== 'done' && 
                   item.scraper_status !== 'failed' && 
                   !item.building_id;
          }
        },
        {
          name: 'add',
          label: 'Add to Project',
          icon: 'add',
          variant: 'default',
          visible: (item) => {
            if (!item.is_imported) {
              return !this.isInProject(item.id);
            }
            if (item.is_imported && item.building_id) {
              return !this.isInProject(item.building_id);
            }
            return false;
          }
        },
        {
          name: 'remove',
          label: 'Remove',
          icon: 'trash',
          variant: 'destructive',
          visible: (item) => {
            if (!item.is_imported) {
              return this.isInProject(item.id);
            }
            if (item.is_imported && item.building_id) {
              return this.isInProject(item.building_id);
            }
            return false;
          }
        }
      ];
    }
  },
  
  mounted() {
    // Fetch projects on mount
    this.fetchProjects();
    
    // Initialize with initialProjectId if provided
    if (this.initialProjectId) {
      this.selectedProjectId = this.initialProjectId;
    }
    
    // Set initial collapsed state based on visible prop
    this.panelCollapsed = !this.visible;
  },
  
  watch: {
    visible(newValue) {
      this.panelCollapsed = !newValue;
    }
  },
  
  beforeDestroy() {
    // Cancel any pending requests
    if (this.projectsAbortController) {
      this.projectsAbortController.abort();
    }
    if (this.propertiesAbortController) {
      this.propertiesAbortController.abort();
    }
    
    // Clear all markers
    this.clearPropertyMarkers();
  },
  
  methods: {
    /**
     * Fetch list of projects from API
     */
    async fetchProjects() {
      // Cancel any pending request
      if (this.projectsAbortController) {
        this.projectsAbortController.abort();
      }
      
      // Create new AbortController for this request
      this.projectsAbortController = new AbortController();
      
      this.loadingProjects = true;
      this.error = null;
      
      try {
        const response = await axios.get('/api/projects', {
          signal: this.projectsAbortController.signal
        });
        
        // Handle different response formats
        this.projects = response.data.data || response.data || [];
        
        console.log('[ProjectFinder] Loaded', this.projects.length, 'projects');
      } catch (error) {
        // Ignore abort errors
        if (error.name === 'AbortError' || error.message === 'canceled') {
          console.log('[ProjectFinder] Projects request cancelled');
          return;
        }
        
        console.error('[ProjectFinder] Error fetching projects:', error);
        this.error = 'Failed to load projects. Please try again.';
        
        // Show toast notification for error
        if (window.toastr) {
          window.toastr.error('Failed to load projects');
        }
      } finally {
        this.loadingProjects = false;
        this.projectsAbortController = null;
      }
    },
    
    /**
     * Handle project selection change
     */
    onProjectSelected(projectId) {
      console.log('[ProjectFinder] Project selected:', projectId);
      
      // Emit event to parent
      this.$emit('project-selected', projectId);
      
      // Fetch properties for selected project
      if (projectId) {
        this.fetchProjectProperties(projectId);
      } else {
        // Clear properties if no project selected
        this.properties = [];
        this.clearPropertyMarkers();
      }
    },
    
    /**
     * Fetch properties for a specific project
     */
    async fetchProjectProperties(projectId) {
      if (!projectId) {
        console.warn('[ProjectFinder] No project ID provided');
        return;
      }
      
      // Cancel any pending request
      if (this.propertiesAbortController) {
        this.propertiesAbortController.abort();
      }
      
      // Create new AbortController for this request
      this.propertiesAbortController = new AbortController();
      
      this.loadingProperties = true;
      this.error = null;
      
      // Clear previous markers before loading new ones
      this.clearPropertyMarkers();
      
      try {
        const response = await axios.get(`/api/projects/${projectId}`, {
          signal: this.propertiesAbortController.signal
        });
        
        // Extract comps/properties from project data
        const projectData = response.data.data || response.data;
        const comps = projectData.comps || [];
        
        // Transform comps data to include location field
        this.properties = comps.map(comp => ({
          ...comp,
          // Create location string from city and state
          location: [comp.city, comp.state].filter(Boolean).join(', ') || '-',
          // Ensure name field is populated
          name: comp.name || comp.address_line_1 || 'Unnamed Property'
        }));
        
        console.log('[ProjectFinder] Loaded', this.properties.length, 'properties for project', projectId);
        
        // Add markers to map
        this.addPropertyMarkers(this.properties);
        
      } catch (error) {
        // Ignore abort errors
        if (error.name === 'AbortError' || error.message === 'canceled') {
          console.log('[ProjectFinder] Properties request cancelled');
          return;
        }
        
        console.error('[ProjectFinder] Error fetching project properties:', error);
        this.error = 'Failed to load properties. Please try again.';
        this.properties = [];
        
        // Show toast notification for error
        if (window.toastr) {
          window.toastr.error('Failed to load project properties');
        }
      } finally {
        this.loadingProperties = false;
        this.propertiesAbortController = null;
      }
    },
    
    /**
     * Toggle panel collapsed/expanded state
     */
    togglePanel() {
      this.panelCollapsed = !this.panelCollapsed;
      this.$emit('panel-toggled', this.panelCollapsed);
    },
    
    /**
     * Reset selection and clear all data
     */
    resetSelection() {
      this.selectedProjectId = null;
      this.properties = [];
      this.clearPropertyMarkers();
    },
    
    /**
     * Clear all property markers from map
     */
    clearPropertyMarkers() {
      console.log('[ProjectFinder] Clearing', this.propertyMarkers.length, 'markers');
      
      // Remove all markers from the map
      this.propertyMarkers.forEach(markerData => {
        if (markerData.marker) {
          markerData.marker.setMap(null);
        }
        if (markerData.infoWindow) {
          markerData.infoWindow.close();
        }
      });
      
      // Clear the array
      this.propertyMarkers = [];
    },
    
    /**
     * Add property markers to the map
     */
    addPropertyMarkers(properties) {
      if (!this.map) {
        console.warn('[ProjectFinder] No map instance available');
        return;
      }
      
      // Clear existing markers first
      this.clearPropertyMarkers();
      
      // Filter properties that have valid coordinates
      const validProperties = properties.filter(prop => {
        return prop.latitude && 
               prop.longitude && 
               !isNaN(parseFloat(prop.latitude)) && 
               !isNaN(parseFloat(prop.longitude));
      });
      
      console.log('[ProjectFinder] Adding', validProperties.length, 'markers out of', properties.length, 'properties');
      
      // Create markers for each valid property
      validProperties.forEach(property => {
        const markerData = this.createPropertyMarker(property);
        if (markerData) {
          this.propertyMarkers.push(markerData);
        }
      });
    },
    
    /**
     * Create a single property marker
     */
    createPropertyMarker(property) {
      if (!this.map || !window.google) {
        console.warn('[ProjectFinder] Google Maps not available');
        return null;
      }
      
      try {
        const lat = parseFloat(property.latitude);
        const lng = parseFloat(property.longitude);
        
        if (isNaN(lat) || isNaN(lng)) {
          console.warn('[ProjectFinder] Invalid coordinates for property:', property.id);
          return null;
        }
        
        // Create marker with red circle icon
        const marker = new google.maps.Marker({
          position: { lat, lng },
          map: this.map,
          title: property.name || property.address_line_1 || 'Property',
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#EF4444', // Red-500
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2
          }
        });
        
        // Create info window (will be implemented in task 7)
        const infoWindow = new google.maps.InfoWindow({
          content: this.createInfoWindowContent(property)
        });
        
        // Add click listener to marker
        marker.addListener('click', () => {
          // Close all other info windows
          this.propertyMarkers.forEach(md => {
            if (md.infoWindow) {
              md.infoWindow.close();
            }
          });
          
          // Open this info window
          infoWindow.open(this.map, marker);
        });
        
        return {
          marker,
          property,
          infoWindow
        };
      } catch (error) {
        console.error('[ProjectFinder] Error creating marker:', error);
        return null;
      }
    },
    
    /**
     * Create info window content for a property
     */
    createInfoWindowContent(property) {
      const name = property.name || property.address_line_1 || 'Unknown Property';
      const address = [
        property.address_line_1,
        property.city,
        property.state,
        property.zip
      ].filter(Boolean).join(', ');
      
      // Determine the building ID for the link
      const buildingId = property.building_id || property.id;
      const detailsUrl = `/buildings/${buildingId}`;
      
      return `
        <div style="padding: 12px; min-width: 220px; font-family: system-ui, -apple-system, sans-serif;">
          <h3 style="margin: 0 0 8px 0; font-size: 15px; font-weight: 600; color: #111827;">${name}</h3>
          ${address ? `<p style="margin: 0 0 12px 0; font-size: 13px; color: #6b7280; line-height: 1.4;">${address}</p>` : ''}
          <div style="margin-bottom: 12px;">
            ${property.total_square_footage ? `<p style="margin: 0 0 4px 0; font-size: 13px; color: #374151;"><strong>Total SF:</strong> ${this.formatNumber(property.total_square_footage)}</p>` : ''}
            ${property.available_square_footage ? `<p style="margin: 0 0 4px 0; font-size: 13px; color: #374151;"><strong>Available SF:</strong> ${this.formatNumber(property.available_square_footage)}</p>` : ''}
            ${property.type?.type ? `<p style="margin: 0; font-size: 13px; color: #374151;"><strong>Type:</strong> ${property.type.type}</p>` : ''}
          </div>
          <a 
            href="${detailsUrl}" 
            target="_blank"
            style="display: inline-block; padding: 6px 12px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; font-size: 13px; font-weight: 500; transition: background 0.2s;"
            onmouseover="this.style.background='#2563eb'"
            onmouseout="this.style.background='#3b82f6'"
          >
            View Details →
          </a>
        </div>
      `;
    },
    
    /**
     * Format number with commas
     */
    formatNumber(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('en-US').format(value);
    },
    
    /**
     * Check if a building is in the current project
     */
    isInProject(buildingId) {
      // Handle both regular and imported building IDs
      if (typeof buildingId === 'string' && buildingId.startsWith('imported_')) {
        return false;
      }
      
      // Check if building is in the properties list
      return this.properties.some(prop => {
        const propId = prop.building_id || prop.id;
        return propId === buildingId;
      });
    },
    
    /**
     * Handle grid action button clicks
     * Routes to appropriate action handler
     */
    handleGridAction({ action, item }) {
      console.log('[ProjectFinder] Grid action:', action, item);
      
      switch (action) {
        case 'add':
          this.addToProject(item);
          break;
        case 'remove':
          this.removeFromProject(item);
          break;
        case 'request':
          this.requestBuildingDetails(item);
          break;
        case 'status':
          this.checkScraperStatus(item);
          break;
        default:
          console.warn('Unknown action:', action);
      }
    },
    
    /**
     * Add a building to the current project
     * Copied from ManageCompsModal
     */
    async addToProject(building) {
      if (!this.selectedProjectId) {
        console.warn('[ProjectFinder] No project selected');
        return;
      }
      
      // Determine the actual building ID to use
      let buildingId = building.id;
      
      // If this is an imported building that has been converted, use the building_id
      if (building.is_imported && building.building_id) {
        buildingId = building.building_id;
      }
      
      // Check if it's an imported building that hasn't been converted yet
      if (building.is_imported && !building.building_id) {
        if (window.toastr) {
          window.toastr.info('This building must be converted from imported data before adding to projects. Please request details first.');
        }
        return;
      }
      
      this.loading = true;
      
      try {
        const response = await axios.post(`/projects/${this.selectedProjectId}/comps`, {
          building_id: buildingId
        });
        
        // Reload properties to reflect the change
        await this.fetchProjectProperties(this.selectedProjectId);
        
        // Show success message
        if (window.toastr) {
          window.toastr.success(response.data.message || 'Building added to project');
        }
        
        // Emit event to parent
        this.$emit('comp-added', response.data.comps);
        
      } catch (error) {
        console.error('[ProjectFinder] Error adding comp:', error);
        
        if (window.toastr) {
          window.toastr.error(error.response?.data?.message || 'Failed to add building to project');
        }
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Remove a building from the current project
     * Copied from ManageCompsModal
     */
    async removeFromProject(building) {
      if (!this.selectedProjectId) {
        console.warn('[ProjectFinder] No project selected');
        return;
      }
      
      this.loading = true;
      
      try {
        // Determine the actual building ID
        let buildingId = building.id;
        if (building.is_imported && building.building_id) {
          buildingId = building.building_id;
        }
        
        // Find the comp ID (pivot ID) for this building
        const comp = this.properties.find(c => (c.building_id || c.id) === buildingId);
        const compId = comp?.pivot?.id || comp?.id;
        
        const response = await axios.delete(`/projects/${this.selectedProjectId}/comps/${compId}`);
        
        // Reload properties to reflect the change
        await this.fetchProjectProperties(this.selectedProjectId);
        
        // Show success message
        if (window.toastr) {
          window.toastr.success(response.data.message || 'Building removed from project');
        }
        
        // Emit event to parent
        this.$emit('comp-removed', response.data.comps);
        
      } catch (error) {
        console.error('[ProjectFinder] Error removing comp:', error);
        
        if (window.toastr) {
          window.toastr.error(error.response?.data?.message || 'Failed to remove building from project');
        }
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Request building details for an imported building
     * Copied from ManageCompsModal
     */
    async requestBuildingDetails(building) {
      if (!building.is_imported) {
        return;
      }
      
      if (!this.selectedProjectId) {
        console.warn('[ProjectFinder] No project selected');
        return;
      }
      
      this.loading = true;
      
      try {
        // Extract the imported building ID from the composite ID
        const importedBuildingId = building.id.toString().replace('imported_', '');
        
        // If this is a retry, show appropriate message
        const isRetry = building.scraper_status === 'failed';
        
        await axios.post(`/api/buildings/scraper/${importedBuildingId}/request`, {
          project_id: this.selectedProjectId // Pass project context for auto-add
        });
        
        if (window.toastr) {
          if (isRetry) {
            window.toastr.success('Scraper request has been requeued. The building will be automatically added to your project once ready.');
          } else {
            window.toastr.success('Scraper request has been queued. The building will be automatically added to your project once ready.');
          }
        }
        
        // Update local building status
        building.scraper_status = 'queued';
        building.scraper_error = null;
        
        // Start polling for status
        this.pollScraperStatus(building, importedBuildingId);
        
      } catch (error) {
        console.error('[ProjectFinder] Error requesting building details:', error);
        
        if (window.toastr) {
          window.toastr.error(error.response?.data?.message || 'Failed to request building details');
        }
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Check the scraper status for an imported building
     * Copied from ManageCompsModal
     */
    async checkScraperStatus(building) {
      if (!building.is_imported) {
        return;
      }
      
      this.loading = true;
      
      try {
        const importedBuildingId = building.id.toString().replace('imported_', '');
        
        const response = await axios.get(`/api/buildings/scraper/${importedBuildingId}/status`);
        
        const status = response.data.status;
        
        // Update local building data
        building.scraper_status = status;
        building.building_id = response.data.building_id;
        
        if (status === 'done') {
          if (window.toastr) {
            window.toastr.success('Building has been created successfully and added to your project!');
          }
          
          // Reload project properties
          if (this.selectedProjectId) {
            await this.fetchProjectProperties(this.selectedProjectId);
          }
        } else if (status === 'failed') {
          if (window.toastr) {
            window.toastr.error(response.data.error || 'Failed to scrape building details');
          }
        } else {
          if (window.toastr) {
            window.toastr.info(`Current status: ${status}. Please check again in a moment.`);
          }
        }
        
      } catch (error) {
        console.error('[ProjectFinder] Error checking scraper status:', error);
        
        if (window.toastr) {
          window.toastr.error(error.response?.data?.message || 'Failed to check scraper status');
        }
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Poll scraper status for an imported building
     * Copied from ManageCompsModal
     */
    pollScraperStatus(building, importedBuildingId, attempts = 0) {
      const maxAttempts = 60; // Poll for up to 10 minutes (10s intervals)
      
      if (attempts >= maxAttempts) {
        return;
      }
      
      setTimeout(async () => {
        try {
          const response = await axios.get(`/api/buildings/scraper/${importedBuildingId}/status`);
          
          const status = response.data.status;
          building.scraper_status = status;
          building.building_id = response.data.building_id;
          
          if (status === 'done') {
            if (window.toastr) {
              window.toastr.success('The building has been successfully created and added to your project!');
            }
            
            // Reload project properties
            if (this.selectedProjectId) {
              await this.fetchProjectProperties(this.selectedProjectId);
            }
          } else if (status === 'failed') {
            if (window.toastr) {
              window.toastr.error(response.data.error || 'Failed to scrape building details');
            }
          } else {
            // Continue polling
            this.pollScraperStatus(building, importedBuildingId, attempts + 1);
          }
        } catch (error) {
          console.error('[ProjectFinder] Error polling scraper status:', error);
        }
      }, 10000); // Poll every 10 seconds
    }
  }
};
</script>
